import { createContext, useState, useEffect, useContext, type PropsWithChildren, Dispatch, SetStateAction } from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { Item } from '../../util/Item';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from "../../util/Batch";
import { useGridApiRef } from "@mui/x-data-grid";
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { businessToday } from "../../util/businessClock";

import { addMoney } from "../../util/quantity";
export interface MovementTask<T> extends BatchTask<T> {
	operation: number;
	source: number;
	destination: number;
}

interface ContextProps {
	pendingTasks: MovementTask<Item>[];
	setPendingTasks: Dispatch<SetStateAction<MovementTask<Item>[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: MovementTask<Item> | undefined;
	items: any[];
	setItems: (items: any[]) => void;
	isModalOpen: boolean;
	setIsModalOpen: (isOpen: boolean) => void;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
}

const MoveContext = createContext<ContextProps | undefined>(undefined);

export const MoveProvider = ({ children }: PropsWithChildren) => {
	const [pendingTasks, setPendingTasks] = useState<MovementTask<Item>[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const apiRef = useGridApiRef();
	const activeTask = pendingTasks?.find((task) => task.active);

	const addNewTask = () => {
		const newTask: MovementTask<Item> = {
			id: uuidv4(),
			items: [],
			active: true,
			date: businessToday(),
			reference: Util.getMoveNumber(),
			receiver: -1,
			source: -1,
			destination: -1,
			operation: -1,
			description: "",
			is_public: false,
			server_v: 0,
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return this.items!.reduce((sum, item) => addMoney(sum, item.total), 0);
			},
		};

		setPendingTasks((prevTasks) =>
			prevTasks.map((task) => ({ ...task, active: false })).concat(newTask)
		);

		setItems([]);
	};

	useEffect(() => {
		if (fetchedTask) {
			const itemRecords = fetchedTask.records.map((record: any) => {
				const item = new Item(
					{
						id: record.item_id,
						item_code: record.item_code,
						item: record.item_name,
						category: record.category,
						price: record.amount,
						quantity: record.quantity,
						isFractional: record.is_fractional === true || record.IsFractional === true || items.find((c: any) => c.id === record.item_id)?.IsFractional === true,
					}
				);

				return item;
			});


			const { batch, date, src_id, dest_id, operation, client_id, reference, description } = fetchedTask.details;
			updateActiveTask(setPendingTasks, {
				...activeTask,
				id: batch,
				items: itemRecords,
				date: date,
				source: src_id,
				destination: dest_id,
				operation: Number(operation),
				receiver: client_id,
				reference: reference,
				description: description,
			});
		}
	}, [fetchedTask]);

	const value: ContextProps = {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		addNewTask,
		activeTask,
		items,
		setItems,
		isModalOpen,
		setIsModalOpen,
		apiRef,
	};

	return (
		<MoveContext.Provider value={value}>
			{children}
		</MoveContext.Provider>
	);
};

export const useMove = () => {
	const context = useContext(MoveContext);

	if (!context) {
		throw new Error('useMove must be used within a MoveProvider');
	}
	return context;
};